// public/scripts/card-renderer.js - Function to create card DOM elements
import { SUIT_SYMBOLS, getCardNumericValue } from './krida-patra-helpers.js';

/**
 * Creates a DOM element representing a playing card, either face up or face down.
 * Relies on CSS classes for layout and appearance (e.g., .card, .card-inner, .card-col, .card-back, .hearts, etc.)
 * 
 * @param {Object} card - The card object (requires suit, value, and optionally visible:boolean).
 * @returns {HTMLElement} The created card DOM element.
 */
export function createCardElement(card) {
    const cardElement = document.createElement('div');
    cardElement.className = 'card'; // Base class

    // Determine visibility: check isVisibleForTurn, then visible, then default to true.
    let resolvedVisibility = true; // Default to true
    if (card) {
        if (typeof card.isVisibleForTurn === 'boolean') {
            resolvedVisibility = card.isVisibleForTurn;
        } else if (typeof card.visible === 'boolean') {
            resolvedVisibility = card.visible;
        }
    }
    const cardIsVisible = resolvedVisibility;
    // console.log('CardRenderer: card object:', JSON.stringify(card, null, 2), 'Resolved cardIsVisible:', cardIsVisible);

    if (cardIsVisible) {
    // console.log('CardRenderer: card is visible, rendering face up card');
      // --- Visible card face rendering ---
      cardElement.classList.add(card.suit); // Add suit class (e.g., 'hearts')

      const cardInner = document.createElement('div');
      cardInner.className = 'card-inner'; // For internal layout

      // Define columns 
      const leftCol = document.createElement('div');
      leftCol.className = 'card-col card-col-left';
      const centerCol = document.createElement('div');
      centerCol.className = 'card-col card-col-center';
      const rightCol = document.createElement('div');
      rightCol.className = 'card-col card-col-right';
      
      // --- Create Upper/Lower divs within columns (Always exists) ---
      const leftColUpper = document.createElement('div');
      leftColUpper.className = 'col-section upper';
      const leftColLower = document.createElement('div');
      leftColLower.className = 'col-section lower';
      leftCol.appendChild(leftColUpper);
      leftCol.appendChild(leftColLower);

      const rightColUpper = document.createElement('div');
      rightColUpper.className = 'col-section upper';
      const rightColLower = document.createElement('div');
      rightColLower.className = 'col-section lower';
      rightCol.appendChild(rightColUpper);
      rightCol.appendChild(rightColLower);
      // --- END Upper/Lower Setup --- 
      

      // Create common elements (value, suit, numeric, center)
      const valueElement = document.createElement('div');
      valueElement.className = 'card-value';
      valueElement.textContent = card.value;

      const suitSymbolElement = document.createElement('div'); 
      suitSymbolElement.className = 'card-suit';
      suitSymbolElement.textContent = SUIT_SYMBOLS[card.suit] || '';
      
      const numericValueElement = document.createElement('div');
      numericValueElement.className = 'card-numeric-value';
      // Only show numeric value for face cards and aces
      const numericValue = card.numericValue || getCardNumericValue(card);
      const isFaceCardOrAce = ['A', 'J', 'Q', 'K'].includes(card.value);
      numericValueElement.textContent = isFaceCardOrAce ? numericValue : '';
      numericValueElement.style.lineHeight = '1.0';
      
      const centerSuitLarge = document.createElement('div'); 
      centerSuitLarge.className = 'card-suit-large';
      centerSuitLarge.textContent = SUIT_SYMBOLS[card.suit] || '';


      // --- Create 4 corner divs (Always exist) ---
      const upperLeft = document.createElement('div');
      upperLeft.className = 'card-corner upper-left'; 
      const lowerLeft = document.createElement('div');
      lowerLeft.className = 'card-corner lower-left';
      const upperRight = document.createElement('div');
      upperRight.className = 'card-corner upper-right';
      const lowerRight = document.createElement('div');
      lowerRight.className = 'card-corner lower-right';

      
      // --- UNIFIED LAYOUT: Populate and Place Corners for all screens --- 
      // This enforces the desktop-style layout consistently.
      
      // Top Left Corner content (Right-Side Up)
      upperLeft.appendChild(valueElement.cloneNode(true));
      upperLeft.appendChild(suitSymbolElement.cloneNode(true)); 
      const numericCloneTop = numericValueElement.cloneNode(true);
      if (numericCloneTop.textContent) {
        numericCloneTop.classList.add('numeric-value-visible'); 
      }
      upperLeft.appendChild(numericCloneTop);
      upperLeft.classList.add('card-corner-left-upright');
      
      // Bottom Right Corner content (Not rotated)
      const lowerRightContent = document.createElement('div'); // Wrapper for rotation
      lowerRightContent.appendChild(valueElement.cloneNode(true));
      lowerRightContent.appendChild(suitSymbolElement.cloneNode(true)); 
      const numericCloneBottom = numericValueElement.cloneNode(true);
       if (numericCloneBottom.textContent) {
        numericCloneBottom.classList.add('numeric-value-visible'); 
      }
      lowerRightContent.appendChild(numericCloneBottom);
      lowerRight.appendChild(lowerRightContent);
      lowerRight.classList.add('card-corner-right-upsidedown');

      // Append corners to column sub-divs
      leftColUpper.appendChild(upperLeft);
      rightColLower.appendChild(lowerRight);
      // --- End Unified Layout ---

      // Center Column (Always populated)
      centerCol.appendChild(centerSuitLarge);

      // Append columns to inner container
      cardInner.appendChild(leftCol);
      cardInner.appendChild(centerCol);
      cardInner.appendChild(rightCol);

      cardElement.appendChild(cardInner);
      // --- End visible card rendering --- 

    } else {
    //   console.log('CardRenderer: card is not visible, rendering card back');
      // --- Render card back ---
      cardElement.classList.add('card-back');
      const cardBackInner = document.createElement('div');
      cardBackInner.className = 'card-back-inner'; // For potential CSS background/pattern
      cardElement.appendChild(cardBackInner);
      // --- End card back rendering --- 
    }

    // Set card data attributes 
    if (card && card.suit && card.value) { 
        cardElement.dataset.suit = card.suit;
        cardElement.dataset.value = card.value;
        // Add visible status to dataset for easier debugging if needed
        cardElement.dataset.visible = cardIsVisible; 
        const numericValue = card.numericValue !== undefined ? card.numericValue : getCardNumericValue(card);
        if (numericValue !== undefined) {
            cardElement.dataset.numericValue = numericValue;
        }
    } else if (card && card.suit === 'hidden') {
        // For placeholders or card backs where original info isn't needed/available for data attributes
        cardElement.dataset.suit = 'hidden';
        cardElement.dataset.value = 'hidden';
        cardElement.dataset.visible = cardIsVisible; // card.visible should be false here
    }

    return cardElement;
} 